import PlanFooterText from "@components/PlanFooterText"
import SubscriptionForm from "@components/SubscriptionForm"
import getPriceForCurrency from "@helpers/getPriceForCurrency"
import haptics from "@helpers/haptics"
import { getOnboardingContext, isHomeFlow } from "@helpers/localStorage"
import localizeCurrency from "@helpers/localizeCurrency"
import { useTranslation } from "react-i18next"
import { Plan, StripePrice } from "src/types"
import { twMerge } from "tailwind-merge"

interface Props {
  bestOffer?: boolean
  currency: string
  isSubmitting: boolean
  periodAmount: number
  periodUnit: string
  plan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  stripeIntroPrice: StripePrice | undefined
  stripePrice: StripePrice | undefined
  timeLeft: string
  isSelected?: boolean
  onSelect?: () => void
}

export default function PlanCard({
  bestOffer,
  currency,
  isSubmitting,
  periodAmount,
  periodUnit,
  plan,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
  timeLeft,
  isSelected,
  onSelect,
}: Props) {
  const { t } = useTranslation()
  const context = getOnboardingContext()
  const plansCopyKey =
    context === "cars"
      ? "pages.checkout.plansCars"
      : context === "pets"
        ? "pages.checkout.plansPets"
        : isHomeFlow()
          ? "pages.checkout.plansHome"
          : "pages.checkout.plans"

  const isOneTime = plan === "one-time"

  const priceData = getPriceForCurrency(
    isOneTime ? stripePrice : stripePrice,
    currency,
  )
  const introPriceData = getPriceForCurrency(
    isOneTime ? stripePrice : stripeIntroPrice,
    currency,
  )

  if (isOneTime) {
    if (!priceData || !stripePrice) return null

    const localizePrice = (price: number) =>
      localizeCurrency({ amountInCents: price, currency: priceData.currency })

    return (
      <div
        className={twMerge(
          "relative overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-200",
          isSelected && "ring-2 ring-black",
          onSelect && "cursor-pointer hover:shadow-xl",
          onSelect && !isSelected && "opacity-40",
        )}
        onClick={() => {
          haptics.trigger("medium")
          onSelect?.()
        }}
      >
        {bestOffer && (
          <div className="relative overflow-hidden bg-black px-6 py-2 text-center">
            <div className="animate-shine absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent" />
            <p className="relative text-base font-semibold tracking-wide text-white uppercase">
              {t(`${plansCopyKey}.mostPopular`)}
            </p>
          </div>
        )}

        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {onSelect && (
                <div className="mt-0.5">
                  <div
                    className={twMerge(
                      "h-6 w-6 rounded-full border-2",
                      isSelected
                        ? "flex items-center justify-center border-black bg-black"
                        : "border-gray-300",
                    )}
                  >
                    {isSelected && (
                      <div className="h-3 w-3 rounded-full bg-white" />
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1 text-left">
                <h3 className="text-lg font-bold text-black sm:text-2xl">
                  {t(`${plansCopyKey}.oneTimeLabel`)}
                </h3>
                <p className="text-text-secondary text-sm">
                  {t(`${plansCopyKey}.oneTimeSubtitle`)}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <p className="text-text-secondary text-base line-through sm:text-lg">
                {localizePrice(priceData.price * 2)}
              </p>
              <p className="text-2xl leading-none font-bold text-black sm:text-4xl">
                {localizePrice(priceData.price)}
              </p>
              <p className="text-text-secondary text-sm">
                {t(`${plansCopyKey}.oneTimeTag`)}
              </p>
            </div>
          </div>

          {timeLeft !== "00:00" && (!onSelect || isSelected) && (
            <div className="mt-4">
              <div className="bg-offer-red rounded-2xl px-6 py-2 text-center">
                <p className="text-sm font-semibold text-white">
                  {t(`${plansCopyKey}.offerEnds`, { countdown: timeLeft })}
                </p>
              </div>
            </div>
          )}

          {!onSelect && (
            <div className="mt-4 space-y-4">
              <SubscriptionForm
                buttonClassName="theme-button-primary-black uppercase"
                buttonTitle={t(`${plansCopyKey}.buyNow`)}
                currency={priceData.currency}
                fullPrice={stripePrice}
                isSubmitting={isSubmitting}
                plan={plan}
                priceId={stripePrice.id}
                setIsSubmitting={setIsSubmitting}
              />

              <PlanFooterText plan={plan} />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!priceData || !introPriceData || !stripeIntroPrice || !stripePrice)
    return null

  // Prices are the total for the plan's full period (e.g. the whole 3 months),
  // matching what's charged. periodAmount only feeds the per-day day count below.
  const pricePerPeriod = priceData.price
  const introPricePerPeriod = introPriceData.price

  const getTotalDays = () => {
    switch (periodUnit) {
      case "week":
        return periodAmount * 7
      case "month":
        return periodAmount * 30
      case "year":
        return 365
      default:
        return 365
    }
  }

  const totalDays = getTotalDays()
  const pricePerDay = introPricePerPeriod / totalDays

  const localizePrice = (price: number) =>
    localizeCurrency({ amountInCents: price, currency: priceData.currency })

  const getCurrencySymbol = () => {
    const formatted = localizeCurrency({
      amountInCents: 100,
      currency: priceData.currency,
    })
    return formatted ? formatted.replace(/[\d.,\s]/g, "") : ""
  }

  return (
    <div
      className={twMerge(
        "relative overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-200",
        isSelected && "ring-2 ring-black",
        onSelect && "cursor-pointer hover:shadow-xl",
        onSelect && !isSelected && "opacity-40",
      )}
      onClick={() => {
        haptics.trigger("medium")
        onSelect?.()
      }}
    >
      {bestOffer && (
        <div className="relative overflow-hidden bg-black px-6 py-2 text-center">
          <div className="animate-shine absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent" />
          <p className="relative text-base font-semibold tracking-wide text-white uppercase">
            {t(`${plansCopyKey}.mostPopular`)}
          </p>
        </div>
      )}

      <div className="px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {onSelect && (
              <div className="mt-0.5">
                <div
                  className={twMerge(
                    "h-6 w-6 rounded-full border-2",
                    isSelected
                      ? "flex items-center justify-center border-black bg-black"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && (
                    <div className="h-3 w-3 rounded-full bg-white" />
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1 text-left">
              <h3 className="text-lg font-bold text-black sm:text-2xl">
                {periodUnit === "year"
                  ? t(`${plansCopyKey}.twelveMonths`)
                  : periodUnit === "month"
                    ? periodAmount === 3
                      ? t(`${plansCopyKey}.threeMonths`)
                      : t(`${plansCopyKey}.oneMonth`)
                    : `${periodAmount}-${t(`${plansCopyKey}.${periodUnit}`, { count: periodAmount })}`}
              </h3>

              <div className="flex items-center gap-2">
                <p className="text-text-secondary text-base line-through sm:text-lg">
                  {localizePrice(pricePerPeriod)}
                </p>

                <p className="text-lg font-bold text-black sm:text-2xl">
                  {localizePrice(introPricePerPeriod)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1">
            <p className="text-2xl leading-none font-bold text-black sm:text-4xl">
              {getCurrencySymbol()}
              {(pricePerDay / 100).toFixed(2)}
            </p>
            <p className="text-text-secondary text-base sm:text-lg">
              {t(`${plansCopyKey}.perDay`)}
            </p>
          </div>
        </div>

        {timeLeft !== "00:00" && (!onSelect || isSelected) && (
          <div className="mt-4">
            <div className="bg-offer-red rounded-2xl px-6 py-2 text-center">
              <p className="text-sm font-semibold text-white">
                {t(`${plansCopyKey}.offerEnds`, { countdown: timeLeft })}
              </p>
            </div>
          </div>
        )}

        {!onSelect && (
          <div className="mt-4 space-y-4">
            <SubscriptionForm
              buttonClassName="theme-button-primary-black uppercase"
              buttonTitle={t(`${plansCopyKey}.subscribeNow`)}
              currency={priceData.currency}
              fullPrice={stripePrice}
              isSubmitting={isSubmitting}
              plan={plan}
              priceId={stripeIntroPrice.id}
              setIsSubmitting={setIsSubmitting}
            />

            <PlanFooterText plan={plan} />
          </div>
        )}
      </div>
    </div>
  )
}
